import { useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '~/utils/cn';
import { formatCentsToBRL } from '~/utils/formatFinance';

import { Text } from '../../atoms/Text/view';
import type { BarChartEvolutionPoint, PropsBarChartEvolution } from './interfaces';

const PRINCIPAL_COLOR = '#1D4733';
const INTEREST_COLOR = '#6DC48E';
const CORNER_R = 10;
const BAR_SEGMENT_GAP = 3;
const INACTIVE_OPACITY = 0.18;
const PAD_X = 4;
const PAD_Y = 12;
const LABEL_H = 22;
const TOOLTIP_EST_W = 210;
const TOOLTIP_MARGIN = 10;

function pathTopRounded(x: number, y: number, w: number, h: number, r: number): string {
  const cr = Math.min(r, h, w / 2);
  return `M ${x + cr} ${y} L ${x + w - cr} ${y} Q ${x + w} ${y} ${x + w} ${y + cr} L ${x + w} ${y + h} L ${x} ${y + h} L ${x} ${y + cr} Q ${x} ${y} ${x + cr} ${y} Z`;
}


function pathFullRounded(x: number, y: number, w: number, h: number, r: number): string {
  const cr = Math.min(r, h / 2, w / 2);
  return `M ${x + cr} ${y} L ${x + w - cr} ${y} Q ${x + w} ${y} ${x + w} ${y + cr} L ${x + w} ${y + h - cr} Q ${x + w} ${y + h} ${x + w - cr} ${y + h} L ${x + cr} ${y + h} Q ${x} ${y + h} ${x} ${y + h - cr} L ${x} ${y + cr} Q ${x} ${y} ${x + cr} ${y} Z`;
}

export default function BarChartEvolution({
  points,
  height = 180,
  className,
}: PropsBarChartEvolution) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? el.clientWidth;
      if (w > 0) setWidth(Math.floor(w));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const chartH = height - PAD_Y - LABEL_H;

  const bars = useMemo(() => {
    if (!points.length || width === 0) return [];
    const maxVal = Math.max(...points.map((p) => p.principal + p.interest), 1);
    const usableW = width - PAD_X * 2;
    const barCount = points.length;
    const barW = usableW / (barCount + barCount * 0.22 - 0.22);
    const barGap = barW * 0.22;

    return points.map((p, i) => {
      const total = p.principal + p.interest;
      const totalH = (total / maxVal) * chartH * 0.88;
      const interestH = total > 0 ? (p.interest / total) * totalH : 0;
      const principalH = totalH - interestH;
      const x = PAD_X + i * (barW + barGap);
      const topY = PAD_Y + chartH - totalH;
      return { x, barW, topY, interestH, principalH, centerX: x + barW / 2 };
    });
  }, [points, width, chartH]);

  const handleClick = (idx: number) => {
    setActiveIdx((prev) => (prev === null ? idx : null));
  };

  const activeBar = activeIdx !== null ? bars[activeIdx] : null;
  const activePoint = activeIdx !== null ? points[activeIdx] : null;
  const svgH = height - LABEL_H;

  const tooltipLeft = activeBar
    ? Math.min(
        Math.max(activeBar.centerX, TOOLTIP_EST_W / 2 + TOOLTIP_MARGIN),
        width - TOOLTIP_EST_W / 2 - TOOLTIP_MARGIN,
      )
    : 0;
  const caretShift = activeBar ? activeBar.centerX - tooltipLeft : 0;
  const caretShiftSafe = Math.max(Math.min(caretShift, TOOLTIP_EST_W / 2 - 18), -(TOOLTIP_EST_W / 2 - 18));

  if (!points.length) {
    return (
      <div
        ref={containerRef}
        className={cn('flex items-center justify-center w-full', className)}
        style={{ height }}
      >
        <Text className="text-bodyMD color-intense-06">Sem dados</Text>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cn('w-full', className)}>
      <div style={{ position: 'relative' }}>
        {activeBar && activePoint && (
          <div
            style={{
              position: 'absolute',
              left: `${tooltipLeft}px`,
              top: `${activeBar.topY - 12}px`,
              transform: 'translateX(-50%) translateY(-100%)',
              zIndex: 20,
              pointerEvents: 'none',
              filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.13))',
            }}
          >
            <div
              style={{
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '10px 14px',
                minWidth: 192,
                position: 'relative',
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 13,
                  color: '#111827',
                  marginBottom: 9,
                  letterSpacing: 0.2,
                }}
              >
                {activePoint.label}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    background: PRINCIPAL_COLOR,
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Financiamento</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>
                  {formatCentsToBRL(activePoint.principal)}
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    background: INTEREST_COLOR,
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Juros</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>
                  {formatCentsToBRL(activePoint.interest)}
                </span>
              </div>

              <div
                style={{
                  borderTop: '1px solid #f1f5f9',
                  paddingTop: 8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontSize: 12, color: '#6b7280' }}>Total</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>
                  {formatCentsToBRL(activePoint.principal + activePoint.interest)}
                </span>
              </div>

              <div
                style={{
                  position: 'absolute',
                  bottom: -7,
                  left: `calc(50% + ${caretShiftSafe}px)`,
                  transform: 'translateX(-50%) rotate(45deg)',
                  width: 13,
                  height: 13,
                  background: 'white',
                  borderRight: '1px solid #e2e8f0',
                  borderBottom: '1px solid #e2e8f0',
                }}
              />
            </div>
          </div>
        )}

        {width > 0 ? (
          <svg
            width={width}
            height={svgH}
            viewBox={`0 0 ${width} ${svgH}`}
            className="block"
          >
            {bars.map((bar, i) => {
              const isActive = activeIdx === i;
              const isDimmed = activeIdx !== null && !isActive;
              const opacity = isDimmed ? INACTIVE_OPACITY : 1;
              const hasInterest = bar.interestH >= 1;
              const hasPrincipal = bar.principalH >= 1;

              return (
                <g
                  key={i}
                  onClick={() => handleClick(i)}
                  style={{ cursor: 'pointer', opacity, transition: 'opacity 0.22s ease' }}
                >
                  <rect
                    x={bar.x}
                    y={PAD_Y}
                    width={bar.barW}
                    height={chartH}
                    fill="transparent"
                  />
                  {hasInterest && hasPrincipal ? (
                    <>
                      <path
                        d={pathFullRounded(bar.x, bar.topY, bar.barW, bar.interestH, CORNER_R)}
                        fill={INTEREST_COLOR}
                      />
                      <path
                        d={pathTopRounded(
                          bar.x,
                          bar.topY + bar.interestH + BAR_SEGMENT_GAP,
                          bar.barW,
                          bar.principalH,
                          CORNER_R,
                        )}
                        fill={PRINCIPAL_COLOR}
                      />
                    </>
                  ) : hasInterest ? (
                    <path
                      d={pathFullRounded(bar.x, bar.topY, bar.barW, bar.interestH, CORNER_R)}
                      fill={INTEREST_COLOR}
                    />
                  ) : hasPrincipal ? (
                    <path
                      d={pathTopRounded(bar.x, bar.topY, bar.barW, bar.principalH, CORNER_R)}
                      fill={PRINCIPAL_COLOR}
                    />
                  ) : (
                    <rect
                      x={bar.x}
                      y={PAD_Y + chartH - 3}
                      width={bar.barW}
                      height={3}
                      rx={1.5}
                      fill={PRINCIPAL_COLOR}
                      opacity={0.3}
                    />
                  )}
                </g>
              );
            })}
          </svg>
        ) : (
          <div style={{ height: svgH }} />
        )}
      </div>

      <div
        style={{
          display: 'flex',
          paddingLeft: PAD_X,
          paddingRight: PAD_X,
          marginTop: 4,
        }}
      >
        {points.map((p, i) => {
          const bar = bars[i];
          const isActive = activeIdx === i;
          return (
            <div
              key={p.label}
              style={{
                flex: 1,
                textAlign: 'center',
                cursor: 'pointer',
              }}
              onClick={() => handleClick(i)}
            >
              <span
                className="text-captionMD color-intense-06"
                style={{
                  color: isActive ? PRINCIPAL_COLOR : undefined,
                  fontWeight: isActive ? 700 : undefined,
                  transition: 'color 0.22s ease',
                }}
              >
                {p.label}
              </span>
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 20,
          marginTop: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div
            style={{ width: 10, height: 10, borderRadius: 2, background: PRINCIPAL_COLOR }}
          />
          <Text className="text-captionMD color-intense-06">Financiamento</Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div
            style={{ width: 10, height: 10, borderRadius: 2, background: INTEREST_COLOR }}
          />
          <Text className="text-captionMD color-intense-06">Juros</Text>
        </div>
      </div>
    </div>
  );
}
